import { useRef, type ChangeEvent } from "react";
import { Button, Icon } from "@common/index";
import { ComponentColor } from "@common/types";
import { ACCEPTED_COA_TYPES } from "../_helpers/productCoaFile";

// One line about the last COA action: something in flight or done (status,
// announced politely), or a failure (alert).
export interface CoaNotice {
    tone: "status" | "error";
    text: string;
}

interface ProductCoaFieldProps {
    /** The COA's file name: the stored one (Edit) or the chosen one (Add); null for none. */
    fileName: string | null;
    /** A line under the name, e.g. when the COA was last written. */
    detail?: string;
    /** Renders the name as a button that downloads the COA. Omitted: plain text. */
    onDownload?: () => void;
    onPick: (file: File) => void;
    onRemove: () => void;
    /** Locks Attach / Replace and Remove. */
    disabled?: boolean;
    /** An action is in flight: locks every control, the download included. */
    busy?: boolean;
    notice?: CoaNotice | null;
}

// The product form's COA row (#476, DecisionLog I3), as the Inventory mockups
// draw it: an outline "COA" button that attaches a file (or replaces the one
// there), the file's name or "No COA attached.", and a red remove. Shared by
// the Add page, which holds the file until the create sends it, and the Edit
// page, which saves each change at once (ProductCoaManager).
export const ProductCoaField = ({
    fileName,
    detail,
    onDownload,
    onPick,
    onRemove,
    disabled = false,
    busy = false,
    notice = null,
}: ProductCoaFieldProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isLocked = disabled || busy;

    const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        // The OS file dialog outlives a re-render: a pick made after the row
        // locked must change nothing.
        if (!isLocked && file) {
            onPick(file);
        }

        // Allow picking the same file again.
        event.target.value = "";
    };

    return (
        <div>
            <div className="flex items-center gap-3">
                <Button
                    color={ComponentColor.Primary}
                    outline
                    className="shrink-0"
                    aria-label={fileName === null ? "Attach COA" : "Replace COA"}
                    disabled={isLocked}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Icon name="attach_file" />
                    COA
                </Button>

                <div className="min-w-0 grow">
                    {fileName === null ? (
                        <span className="text-gray-500">No COA attached.</span>
                    ) : onDownload ? (
                        <button
                            type="button"
                            aria-label={`Download ${fileName}`}
                            title={fileName}
                            disabled={busy}
                            className="link link-hover block max-w-full truncate text-left text-neutral"
                            onClick={onDownload}
                        >
                            {fileName}
                        </button>
                    ) : (
                        <span title={fileName} className="block truncate">
                            {fileName}
                        </span>
                    )}
                    {fileName !== null && detail && (
                        <span className="block text-xs text-gray-500">
                            {detail}
                        </span>
                    )}
                </div>

                {fileName !== null && (
                    <button
                        type="button"
                        aria-label="Remove COA"
                        disabled={isLocked}
                        className="shrink-0 text-error disabled:opacity-50"
                        onClick={onRemove}
                    >
                        <Icon name="cancel" className="text-2xl" />
                    </button>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_COA_TYPES.join(",")}
                className="hidden"
                aria-label="Choose COA file"
                disabled={isLocked}
                onChange={handlePick}
            />

            {/* A polite live region, there before any status is put into it so
                a screen reader announces the status. Not role="status": the
                Add page's photo area owns that role. */}
            <p aria-live="polite" className="text-sm text-gray-500">
                {notice?.tone === "status" ? notice.text : ""}
            </p>
            {notice?.tone === "error" && (
                <p role="alert" className="text-sm text-error">
                    {notice.text}
                </p>
            )}
        </div>
    );
};
